import type { Appointment, Budget, Company } from "../types";
import {
  downloadBudgetPdf,
  type BudgetPdfOptions,
} from "./budgetPdf";

function appointmentDateIso(appointment: Appointment): string {
  const raw = appointment.appointmentDate;
  if (!raw) return appointment.createdAt;
  return raw.includes("T") ? raw : `${raw}T12:00:00.000Z`;
}

/**
 * A pause closes the current step and opens another for the same subservice.
 * The service note should list each subservice once.
 */
function uniqueStepsBySubService<T extends { serviceSubService?: { id?: number } }>(
  steps: T[]
): T[] {
  const seen = new Set<number>();
  return steps.filter((step) => {
    const id = step.serviceSubService?.id;
    if (id == null) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** Maps a completed appointment into the budget PDF shape (no budget id in the document). */
export function appointmentToBudgetDocument(
  appointment: Appointment
): Budget {
  const steps = uniqueStepsBySubService(appointment.progressSteps ?? []);

  return {
    id: appointment.id,
    clientId: appointment.clientId,
    clientCarId: appointment.clientCarId ?? appointment.clientCar?.id ?? null,
    status: "Completed",
    client: appointment.client
      ? {
          id: appointment.client.id,
          name: appointment.client.name,
          phoneNumber: appointment.client.phoneNumber,
        }
      : undefined,
    clientCar: appointment.clientCar
      ? {
          id: appointment.clientCar.id,
          model: appointment.clientCar.model,
          plate: appointment.clientCar.plate,
          brand: appointment.clientCar.brand,
          effort: appointment.clientCar.effort,
        }
      : undefined,
    services: [
      {
        serviceId: appointment.serviceId,
        price: appointment.price,
        service: appointment.service
          ? {
              id: appointment.serviceId,
              name: appointment.service.name,
              description: appointment.service.description,
              price: appointment.service.price,
              duration: appointment.service.duration,
            }
          : undefined,
        subServices: steps.map((step) => ({
          serviceSubServiceId: step.serviceSubService.id,
          subServiceId: step.serviceSubService.subService.id,
          name: step.serviceSubService.subService.name,
          description: step.serviceSubService.subService.description,
          price: step.serviceSubService.price,
          isRequired: step.serviceSubService.isRequired,
        })),
      },
    ],
    total: appointment.price,
    createdAt: appointmentDateIso(appointment),
    updatedAt: appointment.updatedAt || appointment.createdAt,
  };
}

export type AppointmentPdfOptions = Pick<
  BudgetPdfOptions,
  "showServicePrices" | "showSubServicePrices"
>;

export async function downloadAppointmentPdf(
  appointment: Appointment,
  company: Company | null,
  options: AppointmentPdfOptions = {}
): Promise<void> {
  const budgetDoc = appointmentToBudgetDocument(appointment);
  const clientSlug = (appointment.client?.name ?? `cliente-${appointment.clientId}`)
    .replace(/\s+/g, "-")
    .toLowerCase();

  await downloadBudgetPdf(budgetDoc, company, {
    showServicePrices: options.showServicePrices,
    showSubServicePrices: options.showSubServicePrices,
    documentTitle: "Serviços Realizados",
    isPaidConfirmation: false,
    dateLabel: "Data do serviço",
    statusLabel: "Concluído",
    footerText: "Este documento descreve os serviços realizados.",
    fileName: `servicos-realizados-${appointment.id}-${clientSlug}`,
  });
}
